/**
 * Contorno na câmera de quem está falando.
 * O RealtimeKit emite `activeSpeaker` com o peer id quando a voz muda de pessoa.
 * O tile (`rtk-participant-tile`) guarda esse id em `.participant.id`.
 */

export const SPEAKING_ATTR = "data-constela-speaking";

const TILE_TAGS = new Set(["rtk-participant-tile", "dyte-participant-tile"]);

type TileElement = HTMLElement & { participant?: { id?: string } };

export function tilePeerId(el: HTMLElement): string {
  const id = (el as TileElement).participant?.id;
  return typeof id === "string" ? id : "";
}

export function markSpeakingTiles(root: ParentNode, peerId: string): number {
  let marked = 0;
  const visit = (el: HTMLElement) => {
    const tag = el.tagName.toLowerCase();
    if (!TILE_TAGS.has(tag)) return;
    const speaking = peerId.length > 0 && tilePeerId(el) === peerId;
    if (speaking) {
      el.setAttribute(SPEAKING_ATTR, "true");
      marked += 1;
    } else {
      el.removeAttribute(SPEAKING_ATTR);
    }
  };

  const walk = (node: ParentNode) => {
    const elements =
      node instanceof Element
        ? [node, ...Array.from(node.querySelectorAll("*"))]
        : Array.from(node.querySelectorAll("*"));
    for (const el of elements) {
      if (el instanceof HTMLElement) visit(el);
      const shadow = (el as HTMLElement).shadowRoot;
      if (shadow) walk(shadow);
    }
  };

  walk(root);
  return marked;
}

type SpeakingMeeting = {
  participants: {
    lastActiveSpeaker?: string;
    on: (event: "activeSpeaker", handler: (payload: { peerId?: string }) => void) => void;
    removeListener: (event: "activeSpeaker", handler: (payload: { peerId?: string }) => void) => void;
  };
};

/**
 * Liga o contorno e observa tiles novos (paginação, alguém que entra).
 * O SDK só avisa quando a pessoa da vez muda, então o contorno fica
 * até outra voz assumir — o mesmo comportamento do Meet.
 */
export function enableSpeakingOutline(
  rootEl: HTMLElement | null,
  meeting: SpeakingMeeting | null,
): () => void {
  if (!rootEl || !meeting || typeof MutationObserver === "undefined") {
    return () => undefined;
  }

  let peerId = meeting.participants.lastActiveSpeaker || "";
  let queued = false;

  const apply = () => {
    queued = false;
    markSpeakingTiles(rootEl, peerId);
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(apply);
  };

  const onActiveSpeaker = (payload: { peerId?: string }) => {
    if (!payload?.peerId || payload.peerId === peerId) return;
    peerId = payload.peerId;
    schedule();
  };

  meeting.participants.on("activeSpeaker", onActiveSpeaker);
  const observer = new MutationObserver(schedule);
  observer.observe(rootEl, { childList: true, subtree: true });
  const timers = [100, 400, 1200].map((ms) => window.setTimeout(schedule, ms));
  schedule();

  return () => {
    meeting.participants.removeListener("activeSpeaker", onActiveSpeaker);
    observer.disconnect();
    timers.forEach((id) => window.clearTimeout(id));
    markSpeakingTiles(rootEl, "");
  };
}

